import React, { useState, useEffect, useRef, useCallback } from 'react'
import classNames from 'classnames'
import CalendarToolbar from './CalendarToolbar'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import colorFormatter from 'utils/colorFormatter'
import CalendarDayDetailsDialog from './CalendarDayDetailsDialog'

import '@fullcalendar/common/main.css'
import '@fullcalendar/daygrid/main.css'
import '@fullcalendar/timegrid/main.css'

const MOBILE_BREAKPOINT = 768

const EMPTY_EVENTS = []

export const eventColors = {
    red: {
        bg: 'bg-red-50 dark:bg-red-500/10',
        text: 'text-red-500 dark:text-red-100',
        dot: 'bg-red-500',
    },
    orange: {
        bg: 'bg-orange-50 dark:bg-orange-500/10',
        text: 'text-orange-500 dark:text-orange-100',
        dot: 'bg-orange-500',
    },
    amber: {
        bg: 'bg-amber-50 dark:bg-amber-500/10',
        text: 'text-amber-500 dark:text-amber-100',
        dot: 'bg-amber-500',
    },
    yellow: {
        bg: 'bg-yellow-50 dark:bg-yellow-500/10',
        text: 'text-yellow-500 dark:text-yellow-100',
        dot: 'bg-yellow-500',
    },
    lime: {
        bg: 'bg-lime-50 dark:bg-lime-500/10',
        text: 'text-lime-500 dark:text-lime-100',
        dot: 'bg-lime-500',
    },
    green: {
        bg: 'bg-green-50 dark:bg-green-500/10',
        text: 'text-green-500 dark:text-green-100',
        dot: 'bg-green-500',
    },
    emerald: {
        bg: 'bg-emerald-50 dark:bg-emerald-500/10',
        text: 'text-emerald-500 dark:text-emerald-100',
        dot: 'bg-emerald-500',
    },
    teal: {
        bg: 'bg-teal-50 dark:bg-teal-500/10',
        text: 'text-teal-500 dark:text-teal-100',
        dot: 'bg-teal-500',
    },
    cyan: {
        bg: 'bg-cyan-50 dark:bg-cyan-500/10',
        text: 'text-cyan-500 dark:text-cyan-100',
        dot: 'bg-cyan-500',
    },
    sky: {
        bg: 'bg-sky-50 dark:bg-sky-500/10',
        text: 'text-sky-500 dark:text-sky-100',
        dot: 'bg-sky-500',
    },
    blue: {
        bg: 'bg-blue-50 dark:bg-blue-500/10',
        text: 'text-blue-500 dark:text-blue-100',
        dot: 'bg-blue-500',
    },
    indigo: {
        bg: 'bg-indigo-50 dark:bg-indigo-500/10',
        text: 'text-indigo-500 dark:text-indigo-100',
        dot: 'bg-indigo-500',
    },
    purple: {
        bg: 'bg-purple-50 dark:bg-purple-500/10',
        text: 'text-purple-500 dark:text-purple-100',
        dot: 'bg-purple-500',
    },
    fuchsia: {
        bg: 'bg-fuchsia-50 dark:bg-fuchsia-500/10',
        text: 'text-fuchsia-500 dark:text-fuchsia-100',
        dot: 'bg-fuchsia-500',
    },
    pink: {
        bg: 'bg-pink-50 dark:bg-pink-500/10',
        text: 'text-pink-500 dark:text-pink-100',
        dot: 'bg-pink-500',
    },
    rose: {
        bg: 'bg-rose-50 dark:bg-rose-500/10',
        text: 'text-rose-500 dark:text-rose-100',
        dot: 'bg-rose-500',
    },
}

const CalendarView = (props) => {
    const {
        wrapperClass,
        datesSet,
        toolbarActions,
        events = EMPTY_EVENTS,
        eventClick,
        loading = false,
        ...rest
    } = props
    const calendarRef = useRef(null)
    const [selectedDay, setSelectedDay] = useState(null)
    const [isMobile, setIsMobile] = useState(
        window.innerWidth < MOBILE_BREAKPOINT
    )
    const [viewState, setViewState] = useState(() => ({
        date: new Date(),
        title: 'Calendrier',
        type: 'dayGridMonth',
    }))

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)
        }
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    const handleDatePickerChange = useCallback((date) => {
        if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
            return
        }

        calendarRef.current?.getApi().gotoDate(date)
    }, [])

    const handleDatesSet = useCallback(
        (info) => {
            const date = info.view.calendar.getDate()
            setViewState((current) => {
                if (
                    current.date.getTime() === date.getTime() &&
                    current.title === info.view.title &&
                    current.type === info.view.type
                ) {
                    return current
                }

                return {
                    date,
                    title: info.view.title,
                    type: info.view.type,
                }
            })
            datesSet?.(info)
        },
        [datesSet]
    )

    const navigateCalendar = useCallback((direction) => {
        const calendarApi = calendarRef.current?.getApi()
        if (!calendarApi) return

        if (direction === 'previous') calendarApi.prev()
        if (direction === 'next') calendarApi.next()
    }, [])

    const changeCalendarView = useCallback((viewName) => {
        calendarRef.current?.getApi().changeView(viewName)
    }, [])

    const mountDayDetailsButton = useCallback((info) => {
        const frame = info.el.querySelector('.fc-daygrid-day-frame')
        if (!frame) return
        const button = document.createElement('button')
        button.type = 'button'
        // FullCalendar excludes more links from date selection and drag gestures.
        button.className = 'fc-more-link calendar-day-details-button'
        button.textContent = '+'
        const dateLabel = info.date.toLocaleDateString('fr-FR', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        })
        button.setAttribute(
            'aria-label',
            `Voir les rendez-vous du ${dateLabel}`
        )
        button.title = `Détails du ${dateLabel}`
        button.onclick = (event) => {
            event.preventDefault()
            event.stopPropagation()
            setSelectedDay(info.date)
        }
        frame.appendChild(button)
    }, [])

    const unmountDayDetailsButton = useCallback((info) => {
        info.el.querySelector('.calendar-day-details-button')?.remove()
    }, [])

    const showMoreAppointments = useCallback((info) => {
        setSelectedDay(info.date)
        // A truthy non-string suppresses FullCalendar v5's default popover.
        return true
    }, [])

    const openDayAppointment = (event, jsEvent) => {
        const api = calendarRef.current?.getApi()
        const calendarEvent = api?.getEventById(event.id || event._id)
        setSelectedDay(null)
        if (calendarEvent) {
            eventClick?.({
                event: calendarEvent,
                el: jsEvent.currentTarget,
                jsEvent,
                view: api.view,
            })
        }
    }

    return (
        <div className={classNames('calendar', wrapperClass)}>
            <CalendarToolbar
                viewState={viewState}
                toolbarActions={toolbarActions}
                handleDatePickerChange={handleDatePickerChange}
                navigateCalendar={navigateCalendar}
                changeCalendarView={changeCalendarView}
            />

            {loading && (
                <div className="calendar-loading-bar" aria-hidden="true" />
            )}
            <FullCalendar
                ref={calendarRef}
                initialView="dayGridMonth"
                slotMinTime="08:00:00"
                slotMaxTime="20:15:00"
                scrollTime="08:00:00"
                allDaySlot={false}
                nowIndicator
                firstDay={6}
                height={isMobile ? 'auto' : undefined}
                contentHeight={isMobile ? 'auto' : undefined}
                expandRows={!isMobile}
                slotDuration="00:15:00"
                slotLabelInterval="00:30:00"
                slotLabelFormat={{
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false,
                }}
                eventTimeFormat={{
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false,
                }}
                stickyHeaderDates
                dayMaxEvents={3}
                moreLinkContent={(info) =>
                    isMobile ? info.shortText : info.text
                }
                moreLinkClick={showMoreAppointments}
                dayCellDidMount={mountDayDetailsButton}
                dayCellWillUnmount={unmountDayDetailsButton}
                events={events}
                eventClick={eventClick}
                headerToolbar={false}
                footerToolbar={false}
                eventContent={(arg) => {
                    const { extendedProps } = arg.event
                    const { isEnd, isStart } = arg
                    const { darkColor, lightColor } = colorFormatter(
                        extendedProps.eventColor || '#64748b'
                    )
                    return (
                        <div
                            style={{
                                backgroundColor: lightColor,
                                color: darkColor,
                                borderLeftColor: darkColor,
                            }}
                            className={classNames(
                                'custom-calendar-event h-full',
                                isEnd &&
                                    !isStart &&
                                    '!rounded-tl-none !rounded-bl-none !rtl:rounded-tr-none !rtl:rounded-br-none',
                                !isEnd &&
                                    isStart &&
                                    '!rounded-tr-none !rounded-br-none !rtl:rounded-tl-none !rtl:rounded-bl-none'
                            )}
                        >
                            <span
                                className="calendar-event-title"
                                title={arg.event.title}
                            >
                                {arg.event.title}
                            </span>
                        </div>
                    )
                }}
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                {...rest}
                datesSet={handleDatesSet}
            />
            <CalendarDayDetailsDialog
                date={selectedDay}
                events={events}
                onClose={() => setSelectedDay(null)}
                onEventClick={openDayAppointment}
            />
        </div>
    )
}

export default CalendarView
